// src/pages/PhotographySeries.js - Grid + Lightbox version
import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { getSeriesById } from "../data/projects";

const PhotographySeries = () => {
  const { seriesId } = useParams();
  const series = getSeriesById(seriesId);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [isImageLoading, setIsImageLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const nextPhoto = useCallback(() => {
    if (!series) return;
    const nextIndex = (currentPhotoIndex + 1) % series.photos.length;
    setCurrentPhotoIndex(nextIndex);
    setIsImageLoading(true);
  }, [currentPhotoIndex, series]);

  const prevPhoto = useCallback(() => {
    if (!series) return;
    const prevIndex =
      currentPhotoIndex === 0
        ? series.photos.length - 1
        : currentPhotoIndex - 1;
    setCurrentPhotoIndex(prevIndex);
    setIsImageLoading(true);
  }, [currentPhotoIndex, series]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;

      if (e.key === "ArrowLeft") {
        prevPhoto();
      } else if (e.key === "ArrowRight") {
        nextPhoto();
      } else if (e.key === "Escape") {
        closeLightbox();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, nextPhoto, prevPhoto]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (lightboxOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [lightboxOpen]);

  const openLightbox = (index) => {
    setCurrentPhotoIndex(index);
    setLightboxOpen(true);
    setIsImageLoading(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const handleImageLoad = () => {
    setIsImageLoading(false);
  };

  if (!series) {
    return (
      <div className="min-h-screen">
        <div className="h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl mb-4 text-white">Series not found</h1>
            <Link to="/photography" className="text-red-500 hover:underline">
              Back to Photography
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const currentPhoto = series.photos[currentPhotoIndex];

  return (
    <div className="min-h-screen">
      <div className="pt-16 md:pt-44">
        {/* Back button */}
        <div className="container mx-auto px-4 pt-8">
          <Link
            to="/photography"
            className="inline-flex items-center text-white hover:text-red-500 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                clipRule="evenodd"
              />
            </svg>
            Back to Photography
          </Link>
        </div>

        {/* Header */}
        <div className="container mx-auto px-4 pt-8 pb-12">
          <h1 className="text-4xl md:text-5xl font-display font-bold tracking-wider text-center text-white mb-2">
            {series.title}
          </h1>
          <p className="text-center text-gray-300">
            {series.photos.length}{" "}
            {series.photos.length === 1 ? "Photo" : "Photos"}
          </p>
        </div>

        {/* Photo Grid */}
        <div className="container mx-auto px-4 pb-16">
          <div className="grid grid-cols-3 gap-4">
            {series.photos.map((photo, index) => (
              <button
                key={photo.id}
                onClick={() => openLightbox(index)}
                className="group relative aspect-square overflow-hidden bg-gray-900 hover:opacity-90 transition-opacity duration-300"
              >
                <img
                  src={photo.src}
                  alt={photo.alt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                {/* Subtle overlay on hover */}
                <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-20 transition-opacity duration-300" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.95)" }}
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            className="absolute top-4 right-4 md:top-8 md:right-8 z-60 text-white hover:text-red-500 transition-colors p-2"
            onClick={closeLightbox}
            aria-label="Close lightbox"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          {/* Image container - prevent close when clicking image */}
          <div
            className="relative max-w-7xl max-h-[90vh] w-full px-4 md:px-16"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Loading spinner */}
            {isImageLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="h-16 w-16 rounded-full border-t-4 border-b-4 border-red-500 animate-spin"></div>
              </div>
            )}

            {/* Main image */}
            <img
              src={currentPhoto.src}
              alt={currentPhoto.alt}
              className={`w-full h-full object-contain max-h-[80vh] transition-opacity duration-300 ${
                isImageLoading ? "opacity-0" : "opacity-100"
              }`}
              onLoad={handleImageLoad}
              onClick={(e) => e.stopPropagation()}
            />

            {/* Navigation arrows */}
            {series.photos.length > 1 && (
              <>
                <button
                  className="absolute left-0 md:-left-12 top-1/2 transform -translate-y-1/2 text-white hover:text-red-500 transition-colors p-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    prevPhoto();
                  }}
                  aria-label="Previous photo"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>

                <button
                  className="absolute right-0 md:-right-12 top-1/2 transform -translate-y-1/2 text-white hover:text-red-500 transition-colors p-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    nextPhoto();
                  }}
                  aria-label="Next photo"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </>
            )}

            {/* Photo info */}
            <div className="absolute bottom-0 left-0 right-0 text-center pb-4 px-4">
              {currentPhoto.alt && (
                <p
                  className="text-white text-sm md:text-base italic mb-2"
                  style={{ textShadow: "0 2px 4px rgba(0,0,0,0.8)" }}
                >
                  {currentPhoto.alt}
                </p>
              )}
              <p
                className="text-white text-xs"
                style={{ textShadow: "0 2px 4px rgba(0,0,0,0.8)" }}
              >
                {currentPhotoIndex + 1} / {series.photos.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotographySeries;
